/**
 * MqlPacingBar — W55 Hero band component.
 * W57: rewired to query mql_events via /api/marketing/funnel instead of accounts.status proxy.
 *
 * "MQLs THIS QUARTER · Pacing toward Q2 target of 150 (600 annual)"
 * Large number, red progress fill, acceptance rate vs target.
 *
 * Data source: /api/marketing/funnel
 *   counts.MQL  = MQL events in current quarter window
 *   counts.SAL  = SAL events in current quarter window (= BDR acceptance)
 *
 * Targets imported from marketing-targets.ts constants.
 */

import { useEffect, useState } from "react";
import { api, getCurrentTenant } from "@/lib/session";
import {
  MQL_TARGET_ANNUAL,
  MQL_TARGET_BY_QUARTER,
  BDR_ACCEPTANCE_TARGET_PCT,
} from "@/data/marketing-targets";

const TENANT_ID = 1;

function getCurrentQuarter(): 1 | 2 | 3 | 4 {
  const m = new Date().getMonth() + 1;
  if (m <= 3) return 1;
  if (m <= 6) return 2;
  if (m <= 9) return 3;
  return 4;
}

interface MqlPacingData {
  mqlCount: number;
  acceptedCount: number;
  quarterTarget: number;
  annualTarget: number;
  quarter: number;
}

export function MqlPacingBar() {
  const [data, setData] = useState<MqlPacingData | null>(null);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const q = getCurrentQuarter();
    const quarterTarget = MQL_TARGET_BY_QUARTER[q];

    api<{
      counts: { MQL: number | null; SAL: number | null; SQL: number | null; OPPORTUNITY: number | null };
    }>("/api/marketing/funnel")
      .then((resp) => {
        if (cancelled) return;
        setData({
          mqlCount: resp?.counts?.MQL ?? 0,
          acceptedCount: resp?.counts?.SAL ?? 0,
          quarterTarget,
          annualTarget: MQL_TARGET_ANNUAL,
          quarter: q,
        });
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-5 animate-pulse">
        <div className="h-4 w-48 bg-neutral-100 rounded mb-3" />
        <div className="h-10 w-32 bg-neutral-100 rounded mb-3" />
        <div className="h-3 w-full bg-neutral-100 rounded" />
      </div>
    );
  }

  if (!data) return null;

  const pct = Math.min(100, data.quarterTarget > 0 ? Math.round((data.mqlCount / data.quarterTarget) * 100) : 0);
  const acceptancePct = data.mqlCount > 0 ? Math.round((data.acceptedCount / data.mqlCount) * 100) : 0;
  const acceptanceOnTarget = acceptancePct >= BDR_ACCEPTANCE_TARGET_PCT;
  const quarterName = ["Q1", "Q2", "Q3", "Q4"][data.quarter - 1];

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-5">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
        MQLs This Quarter &middot; Pacing toward {quarterName} target of {data.quarterTarget} ({data.annualTarget} annual)
      </div>

      <div className="flex items-baseline gap-3 mb-4">
        <span className="text-4xl font-bold tabular-nums" style={{ color: "#0B0B0F" }}>
          {data.mqlCount}
        </span>
        <span className="text-xl text-neutral-400 font-light">/ {data.quarterTarget}</span>
        <span className="ml-auto text-sm font-medium" style={{ color: pct >= 100 ? "#15803D" : "#DC2626" }}>
          {pct}% of {quarterName} target
        </span>
      </div>

      <div className="relative h-3 w-full rounded-full bg-neutral-100 overflow-hidden mb-3">
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, backgroundColor: "#DC2626" }}
        />
        <div
          className="absolute inset-y-0 w-0.5 bg-neutral-400"
          style={{ left: "100%" }}
        />
      </div>

      <div className="flex items-center justify-between text-sm">
        <span style={{ color: acceptanceOnTarget ? "#15803D" : "#DC2626" }} className="font-medium">
          {acceptancePct}% accepted by BDR
        </span>
        <span className="text-neutral-400">
          target {BDR_ACCEPTANCE_TARGET_PCT}%
        </span>
      </div>
    </div>
  );
}
